/*
 *   File : pending-request.js
 *   Author : https://evoqins.com
 *   Description : Pending request page
 *   Integrations : react-toastify
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'bootstrap';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

//custom component
import { DataTableComponent } from '../../../Component/DataTable';
import { CustomSearchInput, CustomSelectBox } from '../../../Component/FormElements';
import { PrimaryButton, DownloadButton, TextButton } from '../../../Component/Buttons';
import { ManageAssignModal, ManageRedemptionModal, RejectRequestModal } from '../../../Component/Modal';
import { APIService } from '../../../Service';
import { Loader } from '../../../Component/Others';
import { EmptyScreen } from '../../../Component/EmptyComponent';


// Custom data
// Tabs
const REQUEST_TABS = [
    {
        id: 1,
        label: "New joining request"
    },
    {
        id: 2,
        label: "Change request"
    },
];

// Tab 1: CA/registration/list | Tab 2: CA/update-request/list
const TAB_SORT_FIELDS = {
    1: ['name', 'email', 'phone', 'store_name', 'created', 'dob'],
    2: ['customer_name', 'customer_email', 'customer_mobile', 'type', 'status', 'approved_by', 'approved_on', 'created'],
};

const PendingRequest = () => {

    const navigate = useNavigate();
    const { state } = useLocation();

    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);
    const skipDobListFetchRef = useRef(false);

    const [tabIndex, setTabIndex] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [requestData, setRequestData] = useState({});
    const [customerId, setCustomerId] = useState(null);
    const [reasonList, setReasonList] = useState([]);
    const [reasonLoader, setReasonLoader] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [acceptSuccess, setAcceptSuccess] = useState(false);
    const [acceptLoading, setAcceptLoading] = useState(false);

    const [dobChangeRequestData, setDobChangeRequestData] = useState({});
    const [listLoading, setListLoading] = useState(true);
    const [currentPageDob, setCurrentPageDob] = useState(1);

    const [dobChangeSuccess, setDobChangeSuccess] = useState(false);
    const [dobChangeAcceptLoading, setDobChangeAcceptLoading] = useState({});
    const [dobChangeRejectLoading, setDobChangeRejectLoading] = useState({});

    const [dobSearchValue, setDobSearchValue] = useState("");
    const [downloadLoader, setDownloadLoader] = useState(false);
    const [toggleFilter, setToggleFilter] = useState(false);
    const [selected, setSelected] = useState(null);

    const [assignModal, setAssignModal] = useState(false);
    const [assignData, setAssignData] = useState(null);

    const [filterData, setFilterData] = useState({});
    const [filterDataLoader, setFilterDataLoader] = useState(true);
    const [clearFilter, setClearFilter] = useState(false);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    useEffect(() => {
        if (state !== null) {
            setTabIndex(state);
        } else {
            setTabIndex(1);
        }
    }, [state]);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });

        _getRejectionReason();
        _getFilterData();
    }, []);

    useEffect(() => {
        if (skipListFetchRef.current) {
            skipListFetchRef.current = false;
            return;
        }
        _getRegistrationList();
    }, [searchValue, currentPage, clearFilter]);

    useEffect(() => {
        if (skipDobListFetchRef.current) {
            skipDobListFetchRef.current = false;
            return;
        }
        _getDobChangeList();
    }, [dobSearchValue, currentPageDob]);

    useEffect(() => {
        if (acceptSuccess === true) {
            const accept_modal = new Modal(document.getElementById('approve-redemption-modal'));
            accept_modal.show();
        }
    }, [acceptSuccess]);

    useEffect(() => {
        if (customerId != null) {
            const reject_modal = new Modal(document.getElementById('request-reject-modal'));
            reject_modal.show();
        }
    }, [customerId]);

    useEffect(() => {
        if (dobChangeSuccess === true) {
            const accept_modal = new Modal(document.getElementById('approve-redemption-modal'));
            accept_modal.show();
        }
    }, [dobChangeSuccess]);

    // handle tab change 
    const _handleTabChange = (id) => {
        // setTabIndex(id);
        navigate("/manage-request", {
            state: id
        });
        setCurrentPage(1);
        setCurrentPageDob(1);
        setSortField(null);
        setSortDirection(null);
    };

    useEffect(() => {
        if (assignModal === true) {
            const accept_modal = new Modal(document.getElementById('manage-assign-modal'));
            accept_modal.show();
        }
    }, [assignModal]);

    //handle accept request
    const _handleAccept = (data, type) => {
        // setAcceptLoading(prev => ({
        //     ...prev,
        //     [id]: true
        // }));

        setAssignModal(true);
        setAssignData(data);
    };

    //handle reject request
    const _handleReject = (id) => {
        setCustomerId(id);
    };

    // handle view history 
    const _handleViewHistory = () => {
        if (tabIndex === 1) {
            navigate("request-history");
        } else {
            navigate("dob-history");
        }
    };

    //handle accept dob change
    const _handleAcceptDobChange = (id, status) => {
        setDobChangeAcceptLoading(prev => ({
            ...prev,
            [id]: true
        }));

        _manageDobChange(id, status);
    };

    //handle accept dob change
    const _handleRejectDobChange = (id, status) => {
        setDobChangeRejectLoading(prev => ({
            ...prev,
            [id]: true
        }));

        _manageDobChange(id, status);
    };

    // handle page change 
    const _handlePageChange = (page_num) => {
        // Ignore RDT's automatic page reset while we handle sort ourselves
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    };

    // handle page change dob change list 
    const _handlePageChangeDobList = (page_num) => {
        // Ignore RDT's automatic page reset while we handle sort ourselves
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPageDob) {
            return;
        }

        setCurrentPageDob(page_num);

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;
        const allowedSortFields = TAB_SORT_FIELDS[tabIndex] || [];

        if (!sortBy || !allowedSortFields.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);

        // Only skip the list useEffect when page will actually change.
        // If already on page 1, useEffect won't run and a stuck skip flag
        // would block the next pagination API call (loader stuck).
        if (tabIndex === 1) {
            setLoading(true);
            if (currentPage !== 1) {
                skipListFetchRef.current = true;
            }
            setCurrentPage(1);
            _getRegistrationList(1, sortBy, direction);
        } else {
            setListLoading(true);
            if (currentPageDob !== 1) {
                skipDobListFetchRef.current = true;
            }
            setCurrentPageDob(1);
            _getDobChangeList(1, sortBy, direction);
        }

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    const _handleClearAll = () => {
        setClearFilter(true);
        setLoading(true);
        setCurrentPage(1);
        setSortField(null);
        setSortDirection(null);

        setSelected(filterData[0])
    };

    // registration list table column
    const NEW_REQUEST_TABLE_COLUMNS = [
        {
            name: 'Name',
            id: 'name',
            sortField: 'name',
            selector: row => row.name,
            cell: row => <>{row.name}</>,
            width: "260px",
            sortable: true,
        },
        // {
        //     name: 'Email',
        //     selector: row => row.email ? row.email : '-',
        //     width: "280px"
        // },
        {
            name: 'Phone no.',
            id: 'phone',
            sortField: 'phone',
            selector: row => row.phone,
            width: "160px",
            sortable: true,
        },
        {
            name: 'Date of Birth',
            id: 'dob',
            sortField: 'dob',
            selector: row => row.dob,
            cell: row => <>{row.dob}</>,
            format: row => row.dob,
            width: "180px",
            sortable: true,
        },
        {
            name: 'Store',
            id: 'store_name',
            sortField: 'store_name',
            cell: row => <>{row.store_name}</>,
            width: "260px",
            sortable: true,
        },
        {
            name: '',
            cell: (row) =>
                <div className="d-flex gap-0-8">
                    {
                        reasonLoader === false &&
                        <>
                            <PrimaryButton name="Accept & assign to me"
                                // status={acceptLoading[row.id]}
                                className={`padding-6px-tb padding-14px-lr e-accept`}
                                onPress={() => _handleAccept(row, true)} />
                            <PrimaryButton name="Reject"
                                className="padding-6px-tb padding-14px-lr e-reject"
                                onPress={() => _handleReject(row.id)} />
                        </>
                    }
                </div>,

            width: "260px"
        }
    ];

    // DOB change list table column
    const DOB_CHANGE_TABLE_COLUMNS = [
        {
            name: 'Name',
            id: 'customer_name',
            sortField: 'customer_name',
            selector: row => row.customer_name,
            width: "280px",
            sortable: true
        },
        // {
        //     name: 'Email',
        //     selector: row => row.email ? row.email : '-',
        //     width: "280px"
        // },
        // {
        //     name: 'Phone no.',
        //     selector: row => row.customer_mobile,
        //     width: "140px"
        // },
        {
            name: 'Request type',
            id: 'type',
            sortField: 'type',
            cell: (row) => <>{`${row.type_text} change` || "-"}</>,
            width: "140px",
            sortable: true,
        },
        {
            name: 'Current value',
            cell: (row) =>
                <>
                    {format(row.data.old_data, 'PP')}
                </>,
            width: "140px"
        },
        {
            name: 'Requested value',
            cell: (row) =>
                <>
                    {format(row.data.new_data, 'PP')}
                </>,
            width: "160px"
        },
        // {
        //     name: 'Requested value',
        //     cell: (row) => <>{"-"}</>,
        //     width: "160px"
        // },
        {
            name: '',
            cell: (row) =>
                <div className="d-flex gap-2 align-items-center">
                    {
                        reasonLoader === false &&
                        <>
                            <PrimaryButton name="Accept"
                                status={dobChangeAcceptLoading[row.change_request_id]}
                                className={`${dobChangeAcceptLoading[row.change_request_id] ? 'py-1' : 'padding-6px-tb'} padding-14px-lr e-accept`}
                                onPress={() => _handleAcceptDobChange(row.change_request_id, 1)} />
                            <PrimaryButton name="Reject"
                                status={dobChangeRejectLoading[row.change_request_id]}
                                className={`${dobChangeRejectLoading[row.change_request_id] ? 'py-1' : 'padding-6px-tb'} h-100 padding-14px-lr e-reject`}
                                onPress={() => _handleRejectDobChange(row.change_request_id, 2)} />
                        </>
                    }
                </div>,

            width: "200px"
        }
    ];

    // API - get registration list 
    const _getRegistrationList = (page_num = currentPage, sort_by = sortField, sort_order = sortDirection) => {
        let is_post = true;
        let url = 'CA/registration/list';
        let data = {
            "page_num": page_num,
            "query": searchValue,
            "store_id": selected?.value || null,
            "sort_by": sort_by ? [sort_by] : null,
            "order": sort_order ? [sort_order] : null
        };
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setRequestData(response.data);
            } else {
                setRequestData({});
            }
            setLoading(false);
            setClearFilter(false);
        });
    };

    // API - get filter data
    const _getFilterData = () => {
        let is_post = false;
        let url = 'CA/registration/filter-data';
        let data = {};
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {

                let updated_data = response.data.store.map((data) => ({
                    value: data.id,
                    label: data.name
                }));

                const updated_filter_data = [{ value: null, label: "All" }, ...updated_data];

                setFilterData(updated_filter_data);
                setSelected(updated_filter_data[0]);

            } else {
            }
            setFilterDataLoader(false);
        });
    };

    // API - get rejection reason list 
    const _getRejectionReason = () => {
        let is_post = false;
        let url = 'CA/registration/rejection-reasons';
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setReasonList(response.data);
            } else {
            }
            setReasonLoader(false);
        });
    };

    // API - accept request 
    const _manageRequest = (customer_id, type) => {
        setAcceptLoading(true);


        let is_post = true;
        let url = 'CA/registration/manage';
        let data = {
            "customer_id": customer_id,
            "is_approve": true,
            "reason_id": null,
            "reason": null
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setAcceptSuccess(true);

                // setAssignModal(false);
            } else {
                setAcceptSuccess(false);
                toast.dismiss();
                toast.error(response?.data?.message || response?.message, {
                    type: "error",
                    className: "e-toast",
                });
            }

            setAcceptLoading(false);
        });
    };

    // API - get birthday change request list 
    const _getDobChangeList = (page_num = currentPageDob, sort_by = sortField, sort_order = sortDirection) => {
        let is_post = true;
        let url = 'CA/update-request/list';
        let data = {
            "page_num": page_num,
            "query": dobSearchValue,
            "filter": null,
            "sort_by": sort_by ? [sort_by] : null,
            "order": sort_order ? [sort_order] : null
        };
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setDobChangeRequestData(response.data);
            } else { }
            setListLoading(false);
        });
    };

    // API - Manage dob change request 
    const _manageDobChange = (request_id, status) => {
        let is_post = true;
        let url = 'CA/update-request/manage';
        let data = {
            "request_id": request_id,
            "status": status
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                if (status === 1) {
                    setDobChangeSuccess(true);
                } else {
                    toast.dismiss();
                    toast.success("Rejected successfully", {
                        type: "success",
                        className: "e-toast",
                    });
                    _getDobChangeList();
                }
            } else {
                setDobChangeSuccess(false);
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }

            setDobChangeAcceptLoading({});
            setDobChangeRejectLoading({});
        });
    };


    // API - Download data
    const _download = () => {
        setDownloadLoader(true);

        let is_post = false;
        let url = "CA/registration/download";
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const url = response.file_url;
                const link = document.createElement('a');
                link.href = url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    };


    // API - Download data
    const _downloadChangeReq = () => {
        setDownloadLoader(true);

        let is_post = true;
        let url = "CA/update-request/download";
        let data = {
            "filter": null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const url = response.file_url;
                const link = document.createElement('a');
                link.href = url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    };


    return (
        <section className='pb-5 '>
            <div className='e-bg-lavender-blush-light padding-12px-top padding-98px-bottom padding-52px-lr'>
                <h1 className='mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                    Manage Requests
                </h1>
            </div>
            <div className='margin-neg-66px-top padding-52px-lr'>
                <div className='e-bg-white e-admin-box-shadow py-4 e-border-radius-16 e-border-1px-platinum-dark '>
                    <div className='row px-4'>
                        <div className=' col-lg-5 col-md-4 col-5'>
                            <div className="d-flex gap-3">
                                {
                                    REQUEST_TABS.map((tab) => (
                                        <div className={`${tabIndex === tab.id ? 'pe-none e-bg-lust e-text-white border-transparent' : 'e-border-1px-cool-grey e-text-rich-black cursor-pointer'}  e-border-radius-24 `}
                                            onClick={() => _handleTabChange(tab.id)} >
                                            <p className='mb-0 px-4 padding-12px-tb e-alt-font-noto-sans  e-font-14 e-font-weight-500 line-height-14px w-max-content'>
                                                {tab.label}
                                            </p>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                        <div className=' col-lg-7 col-md-8 col-7 text-end ps-0'>
                            <div className='d-flex justify-content-end gap-3 pb-2'>
                                <PrimaryButton name="View history"
                                    className="px-3 py-2 e-font-weight-400 e-bg-alice-blue e-text-raisin-black e-border-radius-8"
                                    onPress={_handleViewHistory} />

                                <DownloadButton loader={downloadLoader}
                                    onClick={() => {
                                        if (tabIndex === 1) {
                                            _download();
                                        } else {
                                            _downloadChangeReq();
                                        }
                                    }} />

                                {tabIndex === 1 ?
                                    // New req list search
                                    <div className='position-relative'>
                                        <CustomSearchInput placeholder="Search by name, id & store"
                                            type="text"
                                            require
                                            value={searchValue}
                                            className="w-100 py-2"
                                            labelStyle="e-font-weight-500"
                                            iconClass="top-9px"
                                            handleChange={(e) => setSearchValue(e)}
                                            handleClear={() => setSearchValue("")} />

                                    </div>
                                    :
                                    // Dob list search
                                    <div className='position-relative'>
                                        <CustomSearchInput placeholder="Search by name, id & store"
                                            type="text"
                                            require
                                            value={dobSearchValue}
                                            className="w-100 py-2"
                                            iconClass="top-9px"
                                            labelStyle="e-font-weight-500"
                                            handleChange={(e) => setDobSearchValue(e)}
                                            handleClear={() => setDobSearchValue("")} />
                                    </div>
                                }

                                {tabIndex === 1 && <img src={toggleFilter ? require("../../../Assets/Image/filter-icon-white.svg").default : require("../../../Assets/Image/filter-icon.svg").default}
                                    draggable={false}
                                    height={40}
                                    width={40}
                                    className={` ${toggleFilter ? `e-bg-indigo` : ``} transition e-border-1px-anti-flash-white e-border-radius-8 p-2 cursor-pointer`}
                                    alt='filter'
                                    onClick={() => setToggleFilter(!toggleFilter)} />
                                }
                            </div>
                        </div>
                    </div>

                    {/* filter */}
                    {tabIndex === 1 && toggleFilter &&
                        <div className='e-bg-anti-flash-white-dark py-3 px-4 mt-3'>
                            <div className='row'>
                                <div className='col-md-3 col-sm-4 col-6'>
                                    <CustomSelectBox label="Store"
                                        optional={true}
                                        value={selected}
                                        options={filterData}
                                        loader={filterDataLoader}
                                        type={2}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        isSearchable={false}
                                        selectChange={(value) => setSelected(value)}
                                    />
                                </div>

                                <div className='col-md-3 col-sm-4 col-6 mt-auto'>
                                    <div className='d-flex gap-3 align-items-center '>
                                        <TextButton name="Clear all"
                                            onPress={_handleClearAll} />
                                        <PrimaryButton name="Filter now"
                                            className="px-3 py-1 e-border-radius-8"
                                            onPress={() => {
                                                _getRegistrationList(1)
                                                setLoading(true);
                                            }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    }

                    {
                        loading ?
                            // loader 
                            <Loader />
                            :

                            <>
                                {/* New request data table */}
                                {
                                    tabIndex == 1 &&
                                    <>
                                        {
                                            Object.keys(requestData).length !== 0 && requestData.total_records > 0 ?
                                                <div className='e-table'>
                                                    <DataTableComponent
                                                        key={`registration-table-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                                        columns={NEW_REQUEST_TABLE_COLUMNS}
                                                        data={requestData.data}
                                                        pagination={true}
                                                        paginationServer={true}
                                                        paginationTotalRows={requestData.total_records}
                                                        paginationDefaultPage={currentPage}
                                                        paginationResetDefaultPage={false}
                                                        sortServer={true}
                                                        defaultSortFieldId={sortField}
                                                        defaultSortAsc={sortDirection !== 'desc'}
                                                        onSort={_handleSort}
                                                        onChangePage={_handlePageChange} />
                                                </div>
                                                :
                                                // empty screen
                                                <div className='h-100vh pt-5 mt-5'>
                                                    <EmptyScreen message="No requests found" />
                                                </div>
                                        }
                                    </>
                                }
                            </>
                    }

                    {listLoading === true ?
                        // loader 
                        <Loader />
                        :
                        <> {/* New request data table */}
                            {tabIndex == 2 &&
                                <>

                                    {
                                        dobChangeRequestData.total_count > 0 ?
                                            <div className='e-table'>
                                                <DataTableComponent
                                                    key={`change-request-table-${currentPageDob}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                                    columns={DOB_CHANGE_TABLE_COLUMNS}
                                                    data={dobChangeRequestData.data}
                                                    pagination={true}
                                                    paginationServer={true}
                                                    paginationTotalRows={dobChangeRequestData.total_count}
                                                    paginationDefaultPage={currentPageDob}
                                                    paginationResetDefaultPage={false}
                                                    sortServer={true}
                                                    defaultSortFieldId={sortField}
                                                    defaultSortAsc={sortDirection !== 'desc'}
                                                    onSort={_handleSort}
                                                    onChangePage={_handlePageChangeDobList} />
                                            </div>
                                            :
                                            // empty screen
                                            <div className='h-100vh pt-5 mt-5'>
                                                <EmptyScreen message="No requests found" />
                                            </div>
                                    }
                                </>
                            }
                        </>
                    }
                </div>
            </div>

            {/* reject request modal */}
            {customerId !== null &&
                <RejectRequestModal
                    customer_id={customerId}
                    reason={reasonList}
                    handleClose={() => setCustomerId(null)}
                    success={() => _getRegistrationList()} />
            }

            {/* approve redemption modal */}
            {acceptSuccess === true &&
                <ManageRedemptionModal
                    description="The selected Sales Specialist has been added under your profile."
                    success={() => {
                        setLoading(true);
                        _getRegistrationList();
                        setAcceptSuccess(false);

                        setAssignModal(false);
                    }} />
            }

            {/* approve dob change modal */}
            {dobChangeSuccess === true &&
                <ManageRedemptionModal
                    description="The request to change the birthday has been accepted."
                    success={() => {
                        setListLoading(true);
                        _getDobChangeList();
                        setDobChangeSuccess(false);
                    }} />
            }

            {assignModal === true &&
                <ManageAssignModal type={2}
                    data={assignData}
                    status={acceptSuccess}
                    buttonLoader={acceptLoading}
                    handleClose={() => {
                        setTimeout(() => {
                            setAssignModal(false);
                        }, 200);
                    }}

                    handleConfirm={() => {
                        _manageRequest(assignData.id);
                    }}
                />
            }


        </section >
    )
}

export default PendingRequest;